import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "./api";

export const ProfileThumb = ({ p, className = "h-24" }) => {
  const [x, y, w, h] = p.bbox;
  const pad = Math.max(w, h) * 0.08;
  return (
    <svg viewBox={`${x - pad} ${y - pad} ${w + 2 * pad} ${h + 2 * pad}`} className={`w-full ${className}`}>
      <path d={p.path} fill="#a1a1aa" fillRule="evenodd" stroke="#e4e4e7" strokeWidth={Math.max(w, h) / 300} />
    </svg>
  );
};

export const roleLabel = (r) => r.replace(/_/g, " ");

const Spec = ({ k, v }) => (
  <div className="flex justify-between border-t border-zinc-800/70 py-1.5 text-sm first:border-t-0"><span className="text-zinc-500">{k}</span><span className="font-mono text-zinc-200">{v}</span></div>
);

const BRAND = { bhoruka: "Bhoruka", bway: "BWAY" };

const ProfileDialog = ({ id, onClose, brand }) => {
  const [p, setP] = useState(null);
  const [compat, setCompat] = useState(null);
  useEffect(() => {
    if (!id) return;
    setP(null);
    api.get(`/profiles/${id}`).then((r) => setP(r.data));
    api.get(`/profiles/${id}/compatible`).then((r) => setCompat(r.data));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-zinc-800 bg-[#121215]" data-testid="profile-dialog">
        {!p ? <Loader2 className="h-6 w-6 animate-spin" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-[Manrope]">{BRAND[brand]} {p.code} <span className="ml-2 font-mono text-xs uppercase text-amber-400">{roleLabel(p.role)}</span></DialogTitle>
              <DialogDescription>{p.description}</DialogDescription>
            </DialogHeader>
            <div className="grid gap-5 md:grid-cols-2">
              <div className="rounded-md border border-zinc-800 bg-[#0c0c0f] p-3"><ProfileThumb p={p} className="h-64" /></div>
              <div>
                <Spec k="Weight (catalogue)" v={`${p.weight_kg_m} kg/m`} />
                <Spec k="Weight (from geometry)" v={`${p.weight_calc_kg_m} kg/m`} />
                <Spec k="Width × Depth" v={`${p.width_mm} × ${p.depth_mm} mm`} />
                <Spec k="Area" v={`${p.area_mm2} mm²`} />
                <Spec k="Ixx / Iyy" v={`${p.ix_cm4} / ${p.iy_cm4} cm⁴`} />
                <Spec k="Zxx / Zyy" v={`${p.zx_cm3} / ${p.zy_cm3} cm³`} />
                <Spec k="Min wall" v={`${p.t_mm} mm`} />
                <Spec k="System" v={p.system_hint || "-"} />
                {p.series && <Spec k="Series" v={p.series.join(", ")} />}
                {p.props_source && <Spec k="Properties from" v={p.props_source === "structural_report" ? "Structural report" : "Traced geometry"} />}
                {p.cust_ref && <Spec k="Cust. ref" v={p.cust_ref} />}
                <div className="mt-3 flex flex-wrap gap-1.5">{p.features.map((f) => <span key={f} className="rounded border border-zinc-700 px-1.5 py-0.5 text-[11px] text-zinc-300">{f}</span>)}</div>
              </div>
            </div>
            {compat && p.frame_member && (
              <div className="space-y-3" data-testid="compatible-profiles">
                {[["transoms", "Matching transoms (same face width)"], ["sleeves", "Splice sleeves that fit the hollow"], ["caps_plates", "Cover caps / pressure plates"]].map(([key, label]) => compat[key]?.length > 0 && (
                  <div key={key}>
                    <div className="mb-1.5 font-mono text-[11px] uppercase text-zinc-500">{label}</div>
                    <div className="flex flex-wrap gap-1.5">{compat[key].slice(0, 12).map((c) => <span key={c.id} className="rounded border border-zinc-700 px-2 py-0.5 font-mono text-[11px] text-zinc-300">{c.code}</span>)}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function ProfileLibrary({ brand = "bhoruka", embedded = false }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/profiles", { params: { brand, role: role || undefined, q: q || undefined, limit: 200 } }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [role, q, brand]);
  const roles = data ? Object.entries(data.roles).sort((a, b) => b[1] - a[1]) : [];
  return (
    <div>
      {embedded ? (
        <p className="mb-6 max-w-3xl text-sm text-zinc-400" data-testid="bway-library-note">AI read each BWAY / Elex section sheet, and the filled view was traced and calibrated to the catalogue area. Section properties come from that real geometry. 150S-L24 uses the values stated in the structural report.</p>
      ) : (
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">Bhoruka Aluminium</div>
        <h1 className="mt-1 font-[Manrope] text-3xl font-extrabold tracking-tight md:text-4xl">Profile Library</h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-400">Every catalogue section was read by AI and traced from the drawing. Section properties are calculated from the real geometry.</p>
      </div>
      )}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-500" />
          <Input data-testid="profile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code or keyword" className="border-zinc-800 bg-zinc-900 pl-8" />
        </div>
        <button data-testid="role-filter-all" onClick={() => setRole("")} className={`rounded border px-2.5 py-1 text-xs ${!role ? "border-sky-500 text-white" : "border-zinc-800 text-zinc-400"}`}>All</button>
        {roles.map(([r, n]) => (
          <button key={r} data-testid={`role-filter-${r}`} onClick={() => setRole(r)} className={`rounded border px-2.5 py-1 text-xs capitalize transition-colors duration-150 ${role === r ? "border-sky-500 bg-sky-500/10 text-white" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{roleLabel(r)} <span className="font-mono text-zinc-500">{n}</span></button>
        ))}
      </div>
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-zinc-500" /> : (
        <>
          <p className="mb-3 font-mono text-xs text-zinc-500" data-testid="profile-count">{data.total} profiles{data.total > 200 ? " (showing 200, refine filter)" : ""}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-7" data-testid="profile-grid">
            {data.items.map((p) => (
              <button key={p.id} data-testid={`profile-card-${p.id}`} onClick={() => setOpen(p.id)} className="rounded-lg border border-zinc-800 bg-[#121215] p-3 text-left transition-colors duration-200 hover:border-sky-500/50">
                <ProfileThumb p={p} />
                <div className="mt-2 flex items-baseline justify-between gap-2">
                  <span className="font-[Manrope] text-sm font-bold text-white">{p.code}</span>
                  <span className="font-mono text-[10px] text-zinc-500">{p.weight_kg_m} kg/m</span>
                </div>
                <div className="truncate text-[11px] capitalize text-amber-400/80">{roleLabel(p.role)}</div>
              </button>
            ))}
          </div>
        </>
      )}
      <ProfileDialog id={open} brand={brand} onClose={() => setOpen(null)} />
    </div>
  );
}
